import axios from "axios";
import {
  createSessionTokenCustom,
  getOcrInterfaceSessionToken,
} from "services/interface";

const AI_SESSION_MINUTES = 99999999;
let aiAuthorization = "";

function extractAuthorization(payload, depth = 0) {
  if (typeof payload === "string") return depth === 0 ? payload.trim() : "";
  if (!payload || typeof payload !== "object" || depth > 3) return "";

  const authorizationEntry = Object.entries(payload).find(
    ([key, value]) =>
      key.toLowerCase() === "authorization" && typeof value === "string",
  );
  if (authorizationEntry) return authorizationEntry[1].trim();

  const wrapperEntry = Object.entries(payload).find(
    ([key, value]) =>
      ["data", "result", "response", "payload"].includes(key.toLowerCase()) &&
      typeof value === "string",
  );
  if (wrapperEntry) return wrapperEntry[1].trim();

  for (const value of Object.values(payload).filter(
    (item) => item && typeof item === "object",
  )) {
    const authorization = extractAuthorization(value, depth + 1);
    if (authorization) return authorization;
  }
  return "";
}

const aiApi = axios.create({
  baseURL: "https://carmen-ocr-backend-xntb.onrender.com/api/v1/carmen",
  timeout: 30000,
});

aiApi.interceptors.request.use((config) => ({
  ...config,
  headers: {
    ...config.headers,
    Authorization: aiAuthorization || localStorage.getItem("AccessToken") || "",
  },
}));

async function createAiDirectAuthorization() {
  const session = await createSessionTokenCustom(AI_SESSION_MINUTES);
  const token = extractAuthorization(session);
  if (!token) {
    throw new Error("Cannot create Carmen session token.");
  }
  return token;
}

function getSelectedCarmenApiUrl() {
  const configuredUrl = window.config.url;
  if (configuredUrl) return configuredUrl;

  const adminToken = localStorage.getItem("adminToken");
  const selectedCompany = window.config.arrCompany?.find(
    (company) => company.adminToken === adminToken,
  );
  return selectedCompany?.apiUrl || window.config.apiUrl;
}

function getCarmenOrigin() {
  const apiUrl = getSelectedCarmenApiUrl();
  try {
    return new URL(apiUrl).origin;
  } catch (error) {
    return apiUrl || "";
  }
}

export function getAiSettingContext() {
  return {
    uri: getCarmenOrigin(),
    bu: localStorage.getItem("Tenant") || "",
  };
}

export async function ensureAiAuthorization() {
  const ocrSession = await getOcrInterfaceSessionToken();
  aiAuthorization = extractAuthorization(ocrSession);
  if (!aiAuthorization) {
    throw new Error("Cannot create OCR interface session token.");
  }

  let tokenStatus;
  let needsTokenRegistration = false;
  try {
    tokenStatus = await getAiTokenStatus();
  } catch (error) {
    if (error?.response?.status !== 401) throw error;
    needsTokenRegistration = true;
  }

  const token = await createAiDirectAuthorization();
  aiAuthorization = token;
  if (needsTokenRegistration) {
    tokenStatus = await updateAiToken(token);
    return { tokenStatus, bankCodes: await getAiBankCodes() };
  }
  return { tokenStatus, bankCodes: null };
}

export async function getAiBankCodes() {
  const { data } = await aiApi.get("/bank-codes");
  return data?.banks || [];
}

export async function getAiSettings() {
  const { data } = await aiApi.get("/settings", {
    params: getAiSettingContext(),
  });
  return data;
}

export async function getAiNotificationStatus() {
  aiAuthorization = await createAiDirectAuthorization();
  const { data } = await aiApi.get("/notifications", {
    params: getAiSettingContext(),
  });
  
  if (data === true) return true;
  return Boolean(
    data &&
    typeof data === "object" &&
    Object.values(data).some((value) => value === true),
  );
}

export async function updateAiSettings(settings) {
  const { data } = await aiApi.put("/settings", {
    ...getAiSettingContext(),
    enabled: Boolean(settings.enabled),
    owner_emails: settings.owner_emails || [],
    tax_ids: settings.tax_ids || [],
    rules: settings.rules || [],
  });
  return data;
}

export async function getAiTokenStatus() {
  const { data } = await aiApi.get("/settings/token", {
    params: getAiSettingContext(),
  });
  return data;
}

export async function updateAiToken(token) {
  const { data } = await aiApi.put("/settings/token", {
    ...getAiSettingContext(),
    token,
  });
  return data;
}

export async function deleteAiToken() {
  await aiApi.delete("/settings/token", { params: getAiSettingContext() });
}
